import { canAccess, canSeeApprovals, hasPermission, type Access } from '../access';

export interface PageShortcut {
  id: string;
  label: string;
  sub: string;
  to: string;
  /** Extra words that should find it ("leave" finds Book holiday). */
  keywords: string;
}

/** Real screens the top-bar search can jump to, filtered to what this person can open. */
export function pageShortcuts(access: Access): PageShortcut[] {
  const all: (PageShortcut | false)[] = [
    canAccess(access, 'hr') && {
      id: 'page:holiday',
      label: 'Book holiday',
      sub: 'HR',
      to: '/hr/holiday',
      keywords: 'holiday leave annual time off request',
    },
    canSeeApprovals(access) && {
      id: 'page:approvals',
      label: 'Holiday approvals',
      sub: 'HR',
      to: '/hr/holiday/approvals',
      keywords: 'approve approvals requests',
    },
    canAccess(access, 'hr') && {
      id: 'page:calendar',
      label: 'Holiday calendar',
      sub: 'HR',
      to: '/hr/calendar',
      keywords: "calendar who's off",
    },
    hasPermission(access, 'absence.own.view') && {
      id: 'page:my-absence',
      label: 'My absence',
      sub: 'HR',
      to: '/hr/absence/me',
      keywords: 'sickness sick absence',
    },
    hasPermission(access, 'attendance.own.view') && {
      id: 'page:my-attendance',
      label: 'My attendance',
      sub: 'HR',
      to: '/hr/attendance/me',
      keywords: 'clocking clock attendance hours',
    },
    hasPermission(access, 'attendance.scope.view') && {
      id: 'page:attendance-today',
      label: 'Attendance today',
      sub: 'HR',
      to: '/hr/attendance',
      keywords: "today clocked in who's in late exceptions attendance",
    },
    hasPermission(access, 'safety.incidents.report') && {
      id: 'page:report',
      label: 'Report an accident or near miss',
      sub: 'Safety',
      to: '/safety/report',
      keywords: 'incident accident near miss injury report',
    },
    canAccess(access, 'people') && {
      id: 'page:people',
      label: 'People directory',
      sub: 'People',
      to: '/people',
      keywords: 'directory staff colleagues',
    },
    canAccess(access, 'fleet') && {
      id: 'page:vehicles',
      label: 'Vehicles',
      sub: 'Fleet',
      to: '/fleet/vehicles',
      keywords: 'vans cars fleet register',
    },
    {
      id: 'page:password',
      label: 'Change password',
      sub: 'Account',
      to: '/account/password',
      keywords: 'password sign in security',
    },
    {
      id: 'page:notifications',
      label: 'Notifications',
      sub: 'Account',
      to: '/notifications',
      keywords: 'notifications alerts bell unread',
    },
    {
      id: 'page:chat',
      label: 'Chat',
      sub: 'Messages',
      to: '/chat',
      keywords: 'chat messages message channels',
    },
    {
      id: 'page:announcements',
      label: 'Announcements',
      sub: 'Operations',
      to: '/announcements',
      keywords: 'news announcements briefing',
    },
  ];
  return all.filter((p): p is PageShortcut => Boolean(p));
}

/** Shortcuts whose label or keywords contain every word typed. */
export function matchPages(pages: readonly PageShortcut[], query: string): PageShortcut[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  return pages
    .filter((p) => {
      const hay = `${p.label} ${p.sub} ${p.keywords}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    })
    .slice(0, 4);
}
